import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus, ShoppingBag, Filter, Eye, XCircle,
  Printer, CreditCard, RefreshCw, X, Tag, Truck, ChevronRight,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { ExportButton } from '../../components/common/ExportButton'
import { ActionMenu } from '../../components/common/ActionMenu'
import { PageHeader } from '../../components/ui/PageHeader'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { useDebounce } from '../../hooks/useDebounce'
import { useUserLocationIds } from '../../hooks/useUserLocations'
import {
  fetchPurchaseOrdersEnhanced, fetchPurchaseOrdersTotal, fetchPurchaseOrdersForExport,
  fetchPaymentAccounts, fetchWarehouses,
  fetchNonRefundPurchaseReturnsTotals, poOutstanding,
  fetchPurchasesTotal, fetchPurchasesDailyTrend, fetchPurchasesMonthlyTrend, fetchAPBalances,
} from '../../lib/supabaseQueries'
import { useDateRange } from '../../hooks/useDateRange'
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey'
import { DateRangePicker } from '../../components/ui/DateRangePicker'
import { displayDate } from '../../utils/dates'
import { supabase } from '../../lib/supabase'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import { CancelPurchaseModal } from '../../components/purchases/CancelPurchaseModal'
import { PurchaseEntryChooserModal, getPreferredPurchaseView, purchaseViewRoute } from '../../components/purchases/PurchaseEntryChooser'
import { SUPPLIER_ADVANCE_MESSAGES, getRandomMessage, formatMessage } from '../../lib/advanceMessages'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

type PORow = {
  id: string; order_number: string; waybill_number: string | null; supplier_invoice_ref: string | null
  total: number; amount_paid: number
  payment_status: string; status: string; delivery_status: string | null; created_at: string
  other_costs: number | null; supplier_id: string | null
  cancel_reason: string | null; cancelled_at: string | null
  suppliers: { id: string; name: string; business_name?: string | null; phone?: string | null } | null
  purchase_order_items: Array<{ quantity: number; quantity_received: number | null }> | null
  purchase_returns: Array<{ id: string }> | null
}

function getPONote(po: PORow): { icon: string; text: string; color: string } | null {
  const hasReturn = (po.purchase_returns?.length ?? 0) > 0
  const items = po.purchase_order_items ?? []
  const hasPartialReceipt = items.some(i => (i.quantity_received ?? 0) > 0 && (i.quantity_received ?? 0) < (i.quantity ?? 0))

  if (po.delivery_status === 'in_transit' && hasPartialReceipt) {
    return { icon: '⚠', text: 'Partially received', color: 'text-amber-600 bg-amber-50' }
  }

  if (po.delivery_status === 'in_transit' || po.status === 'in_transit') {
    return { icon: '🚚', text: 'Goods in transit', color: 'text-blue-600 bg-blue-50' }
  }

  if (hasReturn && po.status !== 'returned') {
    return { icon: '↶', text: 'Partially returned', color: 'text-purple-600 bg-purple-50' }
  }

  if (po.payment_status === 'due' || po.payment_status === 'unpaid' || po.payment_status === 'partial') {
    const daysSince = Math.floor((Date.now() - new Date(po.created_at).getTime()) / (1000 * 60 * 60 * 24))
    if (daysSince > 30) {
      return { icon: '🔴', text: `Payment ${daysSince}d overdue`, color: 'text-red-600 bg-red-50' }
    }
  }

  if (po.status === 'received' && (po.payment_status === 'due' || po.payment_status === 'unpaid')) {
    return { icon: '💳', text: 'Payment pending', color: 'text-gray-600 bg-gray-50' }
  }

  return null
}

// Shape one purchase row for export. Shared by the current-page export and the
// "export all pages" provider so both files have identical columns.
function toPurchaseExportRow(o: PORow) {
  return {
    date: displayDate(o.created_at),
    ref: o.order_number,
    supplier: o.suppliers?.business_name || o.suppliers?.name || '',
    status: o.status,
    payment_status: o.payment_status,
    grand_total: o.total,
    paid: o.amount_paid,
    balance: Math.max(0, o.total - o.amount_paid),
  }
}

// ─── Add Payment Modal ────────────────────────────────────────────────────────

function AddPaymentModal({ open, onClose, order, tenantId, userId, canAddPayment, onSuccess }: {
  open: boolean; onClose: () => void; order: PORow | null
  tenantId: string; userId: string | undefined; canAddPayment: boolean; onSuccess: () => void
}) {
  const { format } = useCurrency()
  const [paymentOption, setPaymentOption] = useState<'full' | 'custom'>('custom')
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const [advanceBalance, setAdvanceBalance] = useState(0)
  const [supplierAPBalance, setSupplierAPBalance] = useState(0)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [advanceMessage, setAdvanceMessage] = useState(() => getRandomMessage(SUPPLIER_ADVANCE_MESSAGES))
  const [submitting, setSubmitting] = useState(false)
  const idem = useIdempotencyKey()

  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ['payment-accounts', tenantId],
    enabled: open && !!tenantId,
    queryFn: () => fetchPaymentAccounts(tenantId),
  })

  const { data: poReturnsTotal = 0 } = useQuery<number>({
    queryKey: ['po-returns-total', tenantId, order?.id],
    enabled: open && !!tenantId && !!order?.id,
    queryFn: async () => {
      const m = await fetchNonRefundPurchaseReturnsTotals(tenantId, [order!.id])
      return m[order!.id] ?? 0
    },
  })

  const supplierId = order?.suppliers?.id
  const supplierName = order?.suppliers?.business_name?.trim() || order?.suppliers?.name?.trim() || 'Supplier'

  useEffect(() => {
    if (!open) return
    setAdvanceMessage(getRandomMessage(SUPPLIER_ADVANCE_MESSAGES))
    setPaymentOption('full')
    setSelectedAccountId('')
    setPaymentAmount('')
    setNote('')
    setPaidOn(new Date().toISOString().slice(0, 10))
    idem.reset()
    if (supplierId) loadSupplierBalance()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierId])

  async function loadSupplierBalance() {
    if (!supplierId) return
    setLoadingBalance(true)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: row } = await (supabase as any)
      .from('suppliers').select('outstanding_balance')
      .eq('tenant_id', tenantId).eq('id', supplierId).maybeSingle()
    const balance = Number(row?.outstanding_balance ?? 0)
    setSupplierAPBalance(balance)
    setAdvanceBalance(balance < 0 ? Math.abs(balance) : 0)
    setLoadingBalance(false)
  }

  if (!order) return null

  const paymentDue = poOutstanding({
    total: order.total,
    amount_paid: order.amount_paid,
    non_refund_returns_total: poReturnsTotal,
    status: order.status,
  })
  const advanceApplied = Math.min(advanceBalance, paymentDue)
  const effectivePayable = supplierAPBalance > 0
    ? Math.min(paymentDue, supplierAPBalance)
    : advanceBalance > 0
      ? Math.max(0, paymentDue - advanceBalance)
      : paymentDue
  const fmsg = formatMessage(advanceMessage, supplierName, format(advanceBalance))

  const fullPayAmount = effectivePayable
  const customAmount = Number(paymentAmount) || 0
  const amountToPay = paymentOption === 'full' ? fullPayAmount : customAmount

  async function handleConfirmPayment() {
    if (!order) return
    if (!canAddPayment) { toast.error('Not authorized to add payment to purchase'); return }
    if (!order.suppliers?.id) { toast.error('Purchase order has no supplier'); return }
    const amount = amountToPay

    // ₦0 case: advance fully covers this PO. Apply the advance instead of
    // posting a cash payment (which would double-pay and create phantom credit).
    if (amount <= 0 && advanceBalance > 0 && paymentDue > 0) {
      setSubmitting(true)
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).rpc('apply_supplier_advance_to_po_atomic', {
          p_tenant_id: tenantId,
          p_supplier_id: order.suppliers.id,
          p_purchase_order_id: order.id,
          p_created_by: userId ?? null,
        })
        if (error) throw new Error(error.message)
        toast.success(`${format(paymentDue)} applied from ${supplierName}'s advance. ✓`)
        onClose(); onSuccess()
      } catch (err: unknown) {
        toast.error('Apply failed: ' + (err instanceof Error ? err.message : String(err)))
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (!selectedAccountId) { toast.error('Please select a payment account'); return }
    if (amount <= 0) { toast.error('Please enter a valid amount'); return }
    setSubmitting(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('post_supplier_payment_atomic', {
        p_tenant_id: tenantId,
        p_supplier_id: order.suppliers.id,
        p_amount: amount,
        p_payment_account_id: selectedAccountId,
        p_payment_date: paidOn,
        p_notes: note || null,
        p_reference: null,
        p_payment_method: 'bank',
        p_purchase_order_id: order.id,
        p_location_id: null,
        p_created_by: userId ?? null,
        p_idempotency_key: idem.get(),
      })
      if (error) throw new Error(error.message)
      idem.reset()
      if (amount === fullPayAmount) {
        toast.success(`Payment complete! ${supplierName} is fully settled. ✓`)
      } else {
        toast.success(`${format(amount)} paid. ${format(Math.max(0, fullPayAmount - amount))} remaining on credit.`)
      }
      onClose(); onSuccess()
    } catch (err: unknown) {
      toast.error('Payment failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Record Payment">
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <p className="text-sm text-gray-500 font-mono">{order.order_number}</p>
          <div className="text-right">
            <p className="text-xs text-gray-500">Payment Due</p>
            <p className="text-xl font-bold text-red-600">{format(paymentDue)}</p>
          </div>
        </div>

        {!loadingBalance && advanceBalance > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{fmsg.emoji}</span>
              <div>
                <p className="font-semibold text-amber-900 text-sm">{fmsg.title}</p>
                <p className="text-amber-700 text-xs mt-0.5">{fmsg.body}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-amber-200 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-amber-700">Payment Due</span>
                <span className="font-medium text-amber-900">{format(paymentDue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-amber-700">Advance Applied ✓</span>
                <span className="font-medium text-green-700">-{format(advanceApplied)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-amber-200 pt-1 mt-1">
                <span className="text-amber-900">Remaining</span>
                <span className={effectivePayable === 0 ? 'text-green-700' : 'text-red-600'}>
                  {format(effectivePayable)}
                </span>
              </div>
            </div>
          </div>
        )}

        {!loadingBalance && effectivePayable < paymentDue && advanceBalance === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            ℹ A {format(paymentDue - effectivePayable)} advance payment on this account reduces your net payable to {format(effectivePayable)}.
          </div>
        )}

        <div className="space-y-2">
          <label className={cn('flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
            paymentOption === 'full' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300')}>
            <input type="radio" name="po_payOpt" value="full"
              checked={paymentOption === 'full'} onChange={() => setPaymentOption('full')}
              className="mt-0.5 accent-orange-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {fullPayAmount === 0 && advanceBalance > 0 && paymentDue > 0 ? 'Apply Advance' : 'Pay in Full'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fullPayAmount === 0 && advanceBalance > 0 && paymentDue > 0
                  ? `${format(paymentDue)} of ${supplierName}'s advance will settle this PO. No cash moves.`
                  : `${format(fullPayAmount)} will be paid now. Nothing left outstanding.`}
              </p>
              {paymentOption === 'full' && !(fullPayAmount === 0 && advanceBalance > 0 && paymentDue > 0) && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Payment Account *</label>
                    <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none">
                      <option value="">— Select —</option>
                      {paymentAccounts.map((acc: { id: string; name: string }) => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Paid On *</label>
                    <input type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Note</label>
                    <input type="text" value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Optional"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                </div>
              )}
            </div>
          </label>

          <label className={cn('flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
            paymentOption === 'custom' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300')}>
            <input type="radio" name="po_payOpt" value="custom"
              checked={paymentOption === 'custom'} onChange={() => setPaymentOption('custom')}
              className="mt-0.5 accent-orange-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Make Payment</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Enter any amount. Pay less = rest stays on credit. Pay more = excess becomes advance balance.
              </p>
              {paymentOption === 'custom' && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Payment Account *</label>
                    <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none">
                      <option value="">— Select —</option>
                      {paymentAccounts.map((acc: { id: string; name: string }) => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Amount *</label>
                    <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                      placeholder="Enter any amount"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                    {customAmount > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {customAmount < fullPayAmount && (
                          <p className="text-xs text-amber-600">{format(fullPayAmount - customAmount)} will remain on credit</p>
                        )}
                        {customAmount === fullPayAmount && (
                          <p className="text-xs text-green-600">✓ Fully settled</p>
                        )}
                        {customAmount > fullPayAmount && (
                          <p className="text-xs text-blue-600">{format(customAmount - fullPayAmount)} will become advance balance on {supplierName}'s account</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Paid On *</label>
                    <input type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Note</label>
                    <input type="text" value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Optional"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleConfirmPayment}
            disabled={
              submitting ||
              (!(paymentOption === 'full' && fullPayAmount === 0 && advanceBalance > 0 && paymentDue > 0) && !selectedAccountId) ||
              (paymentOption === 'custom' && customAmount <= 0) ||
              (paymentOption === 'full' && fullPayAmount === 0 && (advanceBalance <= 0 || paymentDue <= 0))
            }
            className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {submitting ? <Spinner size="sm" /> : (
              paymentOption === 'full'
                ? (fullPayAmount === 0 && advanceBalance > 0 && paymentDue > 0
                    ? `Apply ${format(paymentDue)} from advance`
                    : `Pay ${format(fullPayAmount)}`)
                : customAmount > 0 ? `Pay ${format(customAmount)}` : 'Confirm Payment'
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── View Journals Modal ──────────────────────────────────────────────────────

function ViewJournalsModal({ open, onClose, order, tenantId }: {
  open: boolean; onClose: () => void; order: PORow | null; tenantId: string
}) {
  const { format } = useCurrency()
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['purchase-journals', order?.id, tenantId],
    enabled: open && !!order?.id,
    queryFn: async () => {
      const { data } = await db('journal_entries')
        .select('id, entry_number, entry_date, description, entry_type, journal_entry_lines(debit_amount, credit_amount, description, contact_name, accounts(code, name))')
        .eq('source_id', order!.id)
        .eq('tenant_id', tenantId)
        .order('entry_date', { ascending: true })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []) as any[]
    },
  })

  return (
    <Modal open={open} onClose={onClose} title={`Journal Entries — ${order?.order_number ?? ''}`}>
      {isLoading ? <div className="flex justify-center py-8"><Spinner /></div>
        : entries.length === 0 ? <EmptyState title="No journal entries" description="Accounting entries will appear here." />
        : (
          <div className="space-y-3">
            {entries.map((e: any) => (
              <div key={e.id} className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700 font-mono">{e.entry_number}</span>
                  <span className="text-xs text-gray-500">{displayDate(e.entry_date)} · <span className="capitalize">{e.entry_type}</span></span>
                </div>
                <p className="px-3 py-1.5 text-xs text-gray-600">{e.description}</p>
                <div className="px-3 pb-2 space-y-1">
                  {(e.journal_entry_lines ?? []).map((l: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">{l.accounts?.name ?? '—'} {l.description ? `· ${l.description}` : ''}</span>
                      <span className="font-medium text-gray-700">
                        {l.debit_amount > 0 ? `DR ${format(l.debit_amount)}` : `CR ${format(l.credit_amount)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </Modal>
  )
}

// ─── Purchases Summary ──────────────────────────────────────────────────────

function PurchasesSummary({ tenantId }: { tenantId: string }) {
  const { format } = useCurrency()
  const dr = useDateRange('month')
  const { from, to } = dr
  const [open, setOpen] = useState(true)
  const [chartMode, setChartMode] = useState<'daily' | 'monthly'>('daily')

  const { data: total = 0, isLoading: loadingTotal } = useQuery({
    queryKey: ['purchases-total', tenantId, from, to],
    enabled: !!tenantId,
    queryFn: () => fetchPurchasesTotal(tenantId, from, to),
  })

  const { data: ap = { suppliersPayable: 0, suppliersAdvance: 0 } } = useQuery({
    queryKey: ['purchases-ap-balances', tenantId],
    enabled: !!tenantId,
    queryFn: () => fetchAPBalances(tenantId),
  })

  const { data: dailyTrend = [], isLoading: loadingDaily } = useQuery({
    queryKey: ['purchases-daily-trend', tenantId, from, to],
    enabled: !!tenantId,
    queryFn: () => fetchPurchasesDailyTrend(tenantId, from, to),
  })

  const { data: monthlyTrend = [], isLoading: loadingMonthly } = useQuery({
    queryKey: ['purchases-monthly-trend', tenantId, from, to],
    enabled: !!tenantId,
    queryFn: () => fetchPurchasesMonthlyTrend(tenantId, from, to),
  })

  const chartData = chartMode === 'daily' ? dailyTrend : monthlyTrend
  const chartLoading = chartMode === 'daily' ? loadingDaily : loadingMonthly
  const chartXKey = chartMode === 'daily' ? 'day' : 'month'
  const hasChartData = chartData.some((d) => d.purchases > 0)

  return (
    <section className="rounded-2xl bg-white shadow-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-3 sm:px-4 sm:py-3.5 text-left transition-colors hover:bg-gray-50"
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0"
          style={{ background: '#FAEEDA', color: '#854F0B' }}
        >
          <ShoppingBag className="h-4 w-4" />
        </span>
        <h2 className="text-sm sm:text-base font-semibold text-gray-900 flex-shrink-0">Purchases summary</h2>
        <div className="hidden sm:flex flex-1 items-center gap-4 min-w-0 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">Total</span>
            <span className="font-semibold text-gray-900">{format(total)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">Owed</span>
            <span className="font-semibold text-gray-900">{format(ap.suppliersPayable)}</span>
          </div>
        </div>
        <span className="flex-1 sm:hidden" />
        <ChevronRight
          className={cn(
            'h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200',
            open && 'rotate-90',
          )}
        />
      </button>

      {open && (
        <div className="px-3 pb-3 sm:px-4 sm:pb-4 border-t border-gray-100">
          <div className="pt-3 sm:pt-4 space-y-4">
            <DateRangePicker {...dr} />

            <div className="rounded-xl bg-gray-50 ring-1 ring-black/5 p-3 sm:p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Purchases <span className="text-gray-400 normal-case font-normal">(going out)</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-[11px] text-gray-500">Total Purchases</p>
                  {loadingTotal ? (
                    <div className="h-7 w-24 mt-1 animate-pulse rounded bg-gray-200" />
                  ) : (
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{format(total)}</p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Purchases Owed</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{format(ap.suppliersPayable)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Purchase Advance</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{format(ap.suppliersAdvance)}</p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap mt-4 mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="section-title">Purchases trend</h3>
                  {chartLoading && <Spinner size="sm" />}
                </div>
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setChartMode('daily')}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                      chartMode === 'daily' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setChartMode('monthly')}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                      chartMode === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {chartLoading ? (
                <div className="flex justify-center py-10"><Spinner /></div>
              ) : !hasChartData ? (
                <EmptyState title="No purchases in this period" className="py-8" />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey={chartXKey}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      interval={chartMode === 'daily' ? 4 : 0}
                    />
                    <YAxis hide />
                    <Tooltip
                      formatter={(v: number) => [format(v), 'Purchases']}
                      contentStyle={{ borderRadius: 12, border: 'none', fontSize: 11 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="purchases"
                      name="Purchases"
                      stroke="#f97316"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PurchasesPage() {
  const { format } = useCurrency()
  const { tenant, user } = useAuthStore()
  const { can, viewScope } = usePermissions()
  const scope = viewScope('purchases')
  const ownerId = scope === 'own' ? (user?.id ?? '') : undefined
  const canAddPayment = can('purchases.add_payment')
  const canReceive = can('purchases.receive')
  const canReturn = can('purchases.return')
  const tid = tenant?.id ?? ''
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [filterPurchaseStatus, setFilterPurchaseStatus] = useState('')
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all')
  const [filterLocation, setFilterLocation] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<{
    from?: string; to?: string; purchaseStatus?: string; paymentStatus?: string; locationId?: string
  }>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', tid],
    enabled: !!tid,
    queryFn: () => fetchWarehouses(tid),
  })

  const { data: allowedLocationIds } = useUserLocationIds()
  const isLocationRestricted = !!allowedLocationIds
  const accessibleWarehouses = (isLocationRestricted
    ? warehouses.filter(w => allowedLocationIds!.includes(w.id))
    : warehouses
  ).filter(w => w.is_active !== false)

  useEffect(() => {
    if (!isLocationRestricted) return
    if (filterLocation === '' || !accessibleWarehouses.some(w => w.id === filterLocation)) {
      const first = accessibleWarehouses[0]
      if (first) setFilterLocation(first.id)
    }
  }, [isLocationRestricted, filterLocation, accessibleWarehouses])

  const [payingOrder, setPayingOrder] = useState<PORow | null>(null)
  const [viewingJournals, setViewingJournals] = useState<PORow | null>(null)
  const [cancellingPO, setCancellingPO] = useState<PORow | null>(null)
  const [receivingId, setReceivingId] = useState<string | null>(null)
  const [chooserOpen, setChooserOpen] = useState(false)

  // "Add purchase" entry point (Task 8). If the user previously ticked
  // "always use this view", skip the chooser and go straight there.
  function handleAddPurchase() {
    const { view, always } = getPreferredPurchaseView()
    if (always) navigate(purchaseViewRoute(view))
    else setChooserOpen(true)
  }

  async function handleReceiveGIT(po: PORow) {
    if (!canReceive) {
      toast.error('Not authorized to receive goods')
      return
    }
    if (po.status !== 'in_transit') {
      toast.error('This PO is not in transit')
      return
    }
    const ok = window.confirm(
      `Receive all goods on ${po.order_number}? This adds the full quantity to stock and posts Inventory ↔ Goods-in-Transit.`,
    )
    if (!ok) return

    setReceivingId(po.id)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('receive_goods_in_transit_atomic', {
        p_tenant_id: tid,
        p_purchase_order_id: po.id,
        p_receive_date: new Date().toISOString().slice(0, 10),
        p_received_by: user?.id ?? null,
        p_location_id: null,
      })
      if (error) throw new Error(error.message)
      toast.success(`${po.order_number} received. Stock + journal posted.`)
      qc.invalidateQueries({ queryKey: ['purchase-orders-enhanced'] })
    } catch (err) {
      toast.error('Receive failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setReceivingId(null)
    }
  }

  // Shared filter object for the list page, the footer total and the export.
  const purchaseFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    createdBy: ownerId,
    ...appliedFilters,
  }), [debouncedSearch, ownerId, appliedFilters])

  // Any filter/search change resets to page 1.
  useEffect(() => { setPage(1) }, [purchaseFilters])

  const { data: pageData, isLoading, refetch } = useQuery({
    queryKey: ['purchase-orders-enhanced', tid, purchaseFilters, scope, page, pageSize],
    enabled: !!tid && scope !== 'none',
    queryFn: () => fetchPurchaseOrdersEnhanced(tid, purchaseFilters, { page, pageSize }),
    staleTime: 30 * 1000,
  })
  const orders = pageData?.rows ?? []
  const totalCount = pageData?.count ?? 0

  // Footer total: server-side sum of `total` across ALL matching rows.
  const { data: purchaseTotal = 0 } = useQuery({
    queryKey: ['purchase-orders-total', tid, purchaseFilters, scope],
    enabled: !!tid && scope !== 'none',
    queryFn: () => fetchPurchaseOrdersTotal(tid, purchaseFilters),
    staleTime: 30 * 1000,
  })

  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('highlight')

  useEffect(() => {
    if (!highlightId) return
    const row = document.getElementById(`po-row-${highlightId}`)
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      row.classList.add('ring-2', 'ring-orange-400', 'bg-orange-50/80')
      setTimeout(() => {
        row.classList.remove('ring-2', 'ring-orange-400', 'bg-orange-50/80')
      }, 3000)
    }
  }, [highlightId])

  function applyFilters() {
    setAppliedFilters({
      from: filterFrom || undefined, to: filterTo || undefined,
      purchaseStatus: filterPurchaseStatus || undefined,
      paymentStatus: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
      locationId: filterLocation || undefined,
    })
  }

  function clearFilters() {
    setFilterFrom(''); setFilterTo(''); setFilterPurchaseStatus('')
    setFilterPaymentStatus('all'); setFilterLocation('')
    setAppliedFilters({})
  }

  const hasFilters = !!(appliedFilters.from || appliedFilters.to || appliedFilters.purchaseStatus || appliedFilters.paymentStatus || appliedFilters.locationId)

  const exportData = useCallback(() => (orders as PORow[]).map(toPurchaseExportRow), [orders])

  if (scope === 'none') return <NotAuthorizedView />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Purchases"
        subtitle="Purchase orders from suppliers"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButton
              data={exportData()}
              columns={[
                { key: 'date', header: 'Date' },
                { key: 'ref', header: 'PO #' },
                { key: 'supplier', header: 'Supplier' },
                { key: 'status', header: 'Status' },
                { key: 'payment_status', header: 'Payment Status' },
                { key: 'grand_total', header: 'Total', isCurrency: true },
                { key: 'paid', header: 'Paid', isCurrency: true },
                { key: 'balance', header: 'Balance', isCurrency: true },
              ]}
              filename="purchase-orders"
              fetchData={async () => (await fetchPurchaseOrdersForExport(tid, purchaseFilters)).map(toPurchaseExportRow)}
            />
            <button
              className={cn('btn-secondary flex items-center gap-1.5 text-xs py-1.5', hasFilters && 'border-brand-400 text-brand-600')}
              onClick={() => setFiltersOpen(o => !o)}
            >
              <Filter className="h-3.5 w-3.5" />
              Filters
              {hasFilters && <span className="bg-brand-500 text-white rounded-full h-4 w-4 text-[10px] flex items-center justify-center">!</span>}
            </button>
            <button onClick={handleAddPurchase} className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus className="h-4 w-4" /> Add Purchase
            </button>
          </div>
        }
      />

      <PurchasesSummary tenantId={tid} />

      {/* Search */}
      <div className="relative">
        <input className="input pl-9" placeholder="Search by reference number or supplier…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {search && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch('')}>
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Filters bar */}
      {filtersOpen && (
        <div className="card py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Date From</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">Date To</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div>
              <label className="label">Purchase Status</label>
              <select className="input py-1.5 text-sm" value={filterPurchaseStatus} onChange={e => setFilterPurchaseStatus(e.target.value)}>
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="ordered">Ordered</option>
                <option value="in_transit">In Transit</option>
                <option value="partial">Partial</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="label">Payment Status</label>
              <select className="input py-1.5 text-sm" value={filterPaymentStatus} onChange={e => setFilterPaymentStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="due">Due</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div>
              <label className="label">Location</label>
              <select className="input py-1.5 text-sm" value={filterLocation} onChange={e => setFilterLocation(e.target.value)}>
                {!isLocationRestricted && <option value="">All locations</option>}
                {accessibleWarehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button className="btn-primary py-1.5 text-sm" onClick={applyFilters}>Apply</button>
              <button className="btn-secondary py-1.5 text-sm" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : orders.length === 0 ? (
        <EmptyState
          title={search || hasFilters ? 'No orders match your filters' : 'No purchase orders yet'}
          description={search || hasFilters ? 'Try adjusting your filters.' : 'Create a purchase order to restock your inventory.'}
          icon={<ShoppingBag className="h-8 w-8" />}
          action={!search && !hasFilters ? (
            <button onClick={handleAddPurchase} className="btn-primary inline-flex items-center gap-1.5">
              <Plus className="h-4 w-4" /> Add Purchase
            </button>
          ) : undefined}
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['', 'PO #', 'Date', 'Supplier', 'Location', 'Invoice Ref', 'Total', 'Paid', 'Balance', 'Payment Status', 'Status', 'Notes'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(orders as PORow[]).map(o => {
                  const balance = Math.max(0, o.total - o.amount_paid)
                  return (
                    <tr id={`po-row-${o.id}`} key={o.id} className="border-b border-gray-100 hover:bg-orange-50/30 transition-all duration-500">
                      <td className="px-4 py-3">
                        <ActionMenu items={
                          o.status === 'cancelled'
                            ? [{ label: 'View Details', icon: <Eye className="h-3.5 w-3.5" />, onClick: () => window.location.assign(`/purchases/${o.id}`) }]
                            : [
                              { label: 'View Details', icon: <Eye className="h-3.5 w-3.5" />, onClick: () => window.location.assign(`/purchases/${o.id}`) },
                              { label: 'Print', icon: <Printer className="h-3.5 w-3.5" />, onClick: () => window.print() },
                              { label: 'Labels', icon: <Tag className="h-3.5 w-3.5" />, onClick: () => toast('Coming soon') },
                              ...(o.status === 'in_transit' && canReceive
                                ? [{ label: receivingId === o.id ? 'Receiving…' : 'Receive Goods', icon: <Truck className="h-3.5 w-3.5" />, onClick: () => handleReceiveGIT(o), divider: true }]
                                : []),
                              ...(canAddPayment
                                ? [{ label: 'Add Payment', icon: <CreditCard className="h-3.5 w-3.5" />, onClick: () => setPayingOrder(o), divider: true }]
                                : []),
                              { label: 'View Journals', icon: <Eye className="h-3.5 w-3.5" />, onClick: () => setViewingJournals(o) },
                              ...(canReturn
                                ? [{ label: 'Purchase Return', icon: <RefreshCw className="h-3.5 w-3.5" />, onClick: () => navigate(`/purchases/returns?purchaseId=${o.id}`) }]
                                : []),
                              ...(canReturn
                                ? [{ label: 'Cancel Purchase', icon: <XCircle className="h-3.5 w-3.5" />, onClick: () => setCancellingPO(o), variant: 'danger' as const, divider: true }]
                                : []),
                            ]
                        } />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{o.order_number}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{displayDate(o.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{o.suppliers?.business_name || o.suppliers?.name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{(o as any).warehouses?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">{o.supplier_invoice_ref || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{format(o.total)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {o.amount_paid > 0
                          ? <span className="font-medium text-gray-700">{format(o.amount_paid)}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {balance > 0
                          ? <span className="font-medium text-red-600">{format(balance)}</span>
                          : <span className="text-green-600 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${o.payment_status === 'paid' ? 'badge-green' : o.payment_status === 'partial' ? 'badge-orange' : 'badge-red'} capitalize`}>
                          {o.payment_status === 'unpaid' ? 'Unpaid' : o.payment_status === 'partial' ? 'Partial' : o.payment_status === 'paid' ? 'Paid' : o.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn('badge', {
                            'badge-gray': o.status === 'draft',
                            'badge-blue': o.status === 'ordered' || o.status === 'in_transit',
                            'badge-orange': o.status === 'partial',
                            'badge-green': o.status === 'received',
                            'badge-red': o.status === 'cancelled',
                          })}
                          title={o.status === 'cancelled' && o.cancel_reason
                            ? `Cancelled${o.cancelled_at ? ' · ' + displayDate(o.cancelled_at) : ''} — ${o.cancel_reason}`
                            : undefined}
                        >
                          {o.status === 'in_transit' ? '🚚 In Transit' : o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const note = getPONote(o)
                          if (!note) return <span className="text-gray-300 text-xs">—</span>
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${note.color}`}>
                              {note.icon} {note.text}
                            </span>
                          )
                        })()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 flex justify-between">
            <span>{totalCount} record{totalCount !== 1 ? 's' : ''}</span>
            <span>
              Total: <strong className="text-gray-700">{format(purchaseTotal)}</strong>
            </span>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <AddPaymentModal
        open={!!payingOrder && canAddPayment} onClose={() => setPayingOrder(null)}
        order={payingOrder} tenantId={tid} userId={user?.id} canAddPayment={canAddPayment}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['purchase-orders-enhanced'] })}
      />
      <ViewJournalsModal open={!!viewingJournals} onClose={() => setViewingJournals(null)} order={viewingJournals} tenantId={tid} />

      <CancelPurchaseModal
        open={!!cancellingPO}
        onClose={() => setCancellingPO(null)}
        po={cancellingPO ? {
          id: cancellingPO.id,
          order_number: cancellingPO.order_number,
          total: cancellingPO.total,
          amount_paid: cancellingPO.amount_paid,
          payment_status: cancellingPO.payment_status,
          status: cancellingPO.status,
          delivery_status: cancellingPO.delivery_status,
          supplierName: cancellingPO.suppliers?.business_name || cancellingPO.suppliers?.name || undefined,
        } : null}
        tenantId={tid}
        userId={user?.id}
        onSuccess={() => { refetch(); qc.invalidateQueries({ queryKey: ['purchase-orders-enhanced'] }) }}
      />

      <PurchaseEntryChooserModal open={chooserOpen} onClose={() => setChooserOpen(false)} />

    </div>
  )
}
